import React from "react";
import { cockpit } from "@/api";
import type { Assignee, AssigneeRef, ConversationListItem } from "@/api";
import { useLiveScopeKey } from "@/live/live-provider";
import { useT } from "../i18n";
import { AssigneePicker, principalKey } from "./assignee-picker";

function sameAssignees(left: readonly Assignee[], right: readonly Assignee[]): boolean {
  return left.length === right.length
    && left.every((principal) => right.some((candidate) => principalKey(candidate) === principalKey(principal)));
}

/**
 * Who works on a request, changed from its worklist row. The start-page queue had this picker; with
 * #2003 the queue's rows became the one list, and the picker came along.
 */
export function RowAssignees({
  row,
  currentUserId,
  onChanged,
}: {
  row: ConversationListItem;
  currentUserId: string | null | undefined;
  onChanged: () => void;
}) {
  const t = useT();
  const scopeKey = useLiveScopeKey();
  const [confirmed, setConfirmed] = React.useState<Assignee[] | null>(null);
  const [pending, setPending] = React.useState(false);
  const pendingRef = React.useRef(false);
  const [error, setError] = React.useState(false);
  const scopeRef = React.useRef(scopeKey);
  scopeRef.current = scopeKey;
  const assignees = confirmed && !sameAssignees(confirmed, row.assignees) ? confirmed : row.assignees;

  React.useEffect(() => {
    if (confirmed && sameAssignees(confirmed, row.assignees)) setConfirmed(null);
  }, [confirmed, row.assignees]);

  const change = async (principal: AssigneeRef, action: "add" | "remove") => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setError(false);
    const startedInScope = scopeKey;
    try {
      const result = action === "add"
        ? await cockpit.addRequestAssignee(row.target_request_id, principal)
        : await cockpit.removeRequestAssignee(row.target_request_id, principal);
      if (scopeRef.current !== startedInScope) return;
      if (result.ok !== true || !Array.isArray(result.assignees)) throw new Error("Invalid assignment response");
      setConfirmed(result.assignees);
      onChanged();
    } catch {
      if (scopeRef.current === startedInScope) setError(true);
    } finally {
      pendingRef.current = false;
      if (scopeRef.current === startedInScope) setPending(false);
    }
  };

  return (
    <div className="row-assignees" aria-busy={pending || undefined}>
      <AssigneePicker
        inline
        busy={pending}
        emptyLabel={t("inbox.unassigned")}
        triggerContext={t("inbox.assignee.changeFor", {
          subject: row.subject || t("common.noSubject"),
        })}
        assignees={assignees}
        currentUserId={currentUserId}
        canAssign
        loadOperators={() => cockpit.listAssignees()}
        loadTeams={() => cockpit.listTeams()}
        onAdd={(principal) => { void change(principal, "add"); }}
        onRemove={(principal) => { void change(principal, "remove"); }}
      />
      {error && <span className="row-assignees__error" role="alert">{t("inbox.assignee.failed")}</span>}
    </div>
  );
}
